import { useState } from "react";
import { useMutation, useQuery } from "react-query";

import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";

import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Result,
  Row,
  Space,
  Spin,
  Typography,
  Upload,
  UploadFile,
  Modal,
  Select,
  Alert,
} from "antd";

import InputMask from "react-input-mask";
import { findAddressByCep } from "../../../services/viacep.service";

import ConfirmModal from "../../../components/Commons/Modal/confirm-modal.component";

import { AlertService } from "../../../services/alert.service";
import {
  createAccount,
  getAccountDetails,
  updateAccount,
  isAccountComplete,
} from "../../../services/accounts.service";
import { paymentMethods } from "../../../Enums/PaymentMethod.enums";

const { Text, Title } = Typography;

export default function AccountsPage() {
  const [unblockForm, setUnblockForm] = useState<boolean>(false);
  const [leaveThePage, setLeaveThePage] = useState<boolean>(false);
  const [isEditCompany, setIsEditCompany] = useState<boolean>(false);
  const [accountInitialDetails, setCompanyInitialDetails] = useState<any>({});
  const [valuesChanged, setValuesChanged] = useState<boolean>(false);
  const [thumbnail, setThumbnail] = useState<string>("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [openAddressModal, setOpenAddressModal] = useState<boolean>(false);
  const [openBankModal, setOpenBankModal] = useState<boolean>(false);
  const [addressData, setAddressData] = useState<any>({});
  const [bankData, setBankData] = useState<any>({});
  const [accountUuid, setAccountUuid] = useState<string>("");

  const [accountForm] = Form.useForm();
  const [addressForm] = Form.useForm();
  const [bankForm] = Form.useForm();
  const documentType = Form.useWatch("settings_document_type", accountForm);

  const { refetch: onFindAddressByCep } = useQuery(
    ["loadCepAccountAddress"],
    () => {
      let postalCode = addressForm.getFieldValue("postal_code");
      return findAddressByCep(String(postalCode || "").replaceAll("_", ""));
    },
    {
      onSuccess: (response) => {
        if (response?.data) {
          addressForm.setFieldsValue({
            address: response.data.logradouro,
            neighborhood: response.data.bairro,
            city: response.data.localidade,
            state: response.data.uf,
          });
        }
      },
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );
  const onChangeCep = (value: string) => {
    if ((value || "").length === 9) {
      onFindAddressByCep();
    }
  };

  const {
    isLoading: isloadingGetCompany,
    isRefetching: isRefetchingGetCompany,
    refetch: refetchGetCompany,
    isError: isErrorGetCompany,
  } = useQuery(["getAccountDetails"], () => getAccountDetails(), {
    onSuccess: (response: any) => {
      if (response?.data) {
        handleFormInitialValues(response?.data);
      }
    },
    refetchOnWindowFocus: false,
  });

  const { mutate: createAccountMutate, isLoading: createAccountLoading } =
    useMutation(createAccount, {
      onSuccess: () => {
        refetchGetCompany();
        setUnblockForm(false);
      },
    });

  const { mutate: updateAccountMutate, isLoading: updateAccountLoading } =
    useMutation(updateAccount, {
      onSuccess: () => {
        AlertService.sendAlert([
          { text: "Dados salvos com sucesso.", type: "success" },
        ]);

        refetchGetCompany();
        setUnblockForm(false);
      },
    });

  const handleFormInitialValues = (account: any) => {
    let initialValue: Record<string, any> = {};
    const notificationTime = account?.notification_time;
    const [hours, minutes] = notificationTime
      ? notificationTime.split(":")
      : [null, null];

    initialValue = {
      id: account.id,
      name: account.name,
      phone: account.phone,
      email: account.email,
    };

    setThumbnail(account?.thumbnail);
    setCompanyInitialDetails(account);
    setIsEditCompany(true);

    const settings =
      typeof account?.settings === "string"
        ? JSON.parse(account?.settings)
        : account?.settings || {};

    initialValue.settings_document_type = settings?.document_type || "CNPJ";
    initialValue.settings_document = settings?.document || "";
    initialValue.settings_payment_methods = settings?.payment_methods || [];
    initialValue.settings_email_frequency_days =
      settings?.email_frequency_days || [];
    initialValue.settings_subscription_type =
      settings?.subscription_type || "Free";

    setAddressData(settings?.address || {});
    setBankData(settings?.bank || {});
    addressForm.setFieldsValue(settings?.address || {});
    bankForm.setFieldsValue(settings?.bank || {});

    accountForm.setFieldsValue(initialValue);

    setAccountUuid(account?.uuid);
  };

  const onSubmit = () => {
    accountForm
      .validateFields()
      .then((values: any) => {
        values.id = accountInitialDetails?.id;

        if (fileList.length) {
          values.thumbnail = fileList[0];
        } else {
          delete values.thumbnail;
        }

        const settings = {
          document_type: values.settings_document_type,
          document: values.settings_document,
          address: addressData,
          bank: bankData,
          payment_methods: values.settings_payment_methods || [],
          email_frequency_days: values.settings_email_frequency_days || [],
        };

        values.settings = settings;

        updateAccountMutate(values);
      })
      .catch(() =>
        AlertService.sendAlert([
          { text: "Campo(s) com preenchimento inválido", type: "error" },
        ])
      );
  };

  const handleCancelEdit = () => {
    setLeaveThePage(false);
    setValuesChanged(false);
    setIsEditCompany(false);
    setUnblockForm(false);

    if (Object.keys(accountInitialDetails).length > 0) {
      handleFormInitialValues(accountInitialDetails);
    } else {
      accountForm.resetFields();
      setThumbnail("");
    }
  };

  const getBase64 = (img: any, callback: (url: string) => void) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result as string));
    reader.readAsDataURL(img);
  };

  const handleChange = async ({ file: file }: any) => {
    try {
      const allowedExtensions = [".jpg", ".jpeg", ".png"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if ((file.size || 0) > 2 * 1024 * 1024) {
        AlertService.sendAlert([
          { text: `${file.name} tem mais de 2MB.`, type: "error" },
        ]);

        return false;
      }

      if (!allowedExtensions.includes(fileExtension)) {
        AlertService.sendAlert([
          {
            text: `Apenas são permitidas imagens do tipo: ${allowedExtensions.join(
              ", "
            )}`,
            type: "error",
          },
        ]);

        return false;
      }

      getBase64(file, (url) => setThumbnail(url));

      setFileList([file]);
    } catch (error) {
      console.log("error", error);
      AlertService.sendAlert([
        { text: "Erro ao fazer upload da imagem.", type: "error" },
      ]);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  return (
    <>
      <div className="content_page">
        <Card>
          {isErrorGetCompany ? (
            <Result
              status="error"
              title="Erro ao carregar os dados da conta."
              extra={<Text>Por favor tente mais tarde.</Text>}
            />
          ) : (
            <>
              {isloadingGetCompany || isRefetchingGetCompany ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 32 }} spin />
                    }
                  />
                </div>
              ) : (
                <>
                  {!isAccountComplete(accountInitialDetails) && (
                    <Alert
                      type="warning"
                      showIcon
                      message="Antes de continuar, complete os dados obrigatórios da sua conta."
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <Text
                    style={{ color: "#0899d1", fontWeight: 600, fontSize: 20 }}
                  >
                    Dados da conta
                  </Text>
                  <Form
                    disabled={!unblockForm}
                    form={accountForm}
                    name="create_users"
                    layout="vertical"
                    className="form_table"
                    autoComplete="off"
                    onValuesChange={() => setValuesChanged(true)}
                  >
                    <Row gutter={20} style={{ marginTop: 20 }}>
                      <Col
                        lg={{ span: 6 }}
                        md={{ span: 6 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#FAFAFA",
                            border: "1px solid #F0F0F0",
                            borderRadius: 8,
                            marginTop: 20,
                            height: "100%",
                          }}
                        >
                          <Title
                            level={5}
                            style={{ margin: "0px 0px 20px 0px" }}
                          >
                            Logo da conta
                          </Title>
                          <Form.Item
                            name="image"
                            rules={[
                              {
                                required:
                                  !isEditCompany ||
                                  (isEditCompany && !thumbnail),
                                message: "Campo obrigatório.",
                              },
                            ]}
                          >
                            <Upload
                              name="img"
                              listType="picture-card"
                              className="upload-image"
                              showUploadList={false}
                              beforeUpload={() => false}
                              onChange={handleChange}
                              maxCount={1}
                              multiple={false}
                              onRemove={() => setThumbnail("")}
                              fileList={fileList}
                            >
                              {thumbnail ? (
                                <img
                                  src={thumbnail}
                                  alt="avatar"
                                  style={{ width: "100%" }}
                                />
                              ) : (
                                uploadButton
                              )}
                            </Upload>
                          </Form.Item>
                          <Text style={{ fontSize: "12", color: "#595959" }}>
                            JPG, JPEG e PNG de até 2mb
                          </Text>
                        </div>
                      </Col>
                      <Col
                        lg={{ span: 18 }}
                        md={{ span: 18 }}
                        sm={{ span: 24 }}
                        xs={{ span: 24 }}
                      >
                        <Row gutter={20} style={{ marginTop: 20 }}>
                          <Col span={24}>
                            <Form.Item
                              name="name"
                              label="Nome"
                              rules={[
                                {
                                  required: true,
                                  message: "Campo obrigatório.",
                                },
                              ]}
                            >
                              <Input
                                size="large"
                                maxLength={100}
                                placeholder="100 Contas"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={20} style={{ marginTop: 15 }}>
                          <Col
                            lg={{ span: 8 }}
                            md={{ span: 8 }}
                            sm={{ span: 24 }}
                            xs={{ span: 24 }}
                          >
                            <Form.Item
                              label="Telefone"
                              name="phone"
                              rules={[
                                {
                                  required: true,
                                  message: "Campo obrigatório.",
                                },
                              ]}
                            >
                              <InputMask
                                mask={"(99) 99999-9999"}
                                autoComplete="off"
                              >
                                <Input size="large" style={{ width: "100%" }} />
                              </InputMask>
                            </Form.Item>
                          </Col>
                          <Col
                            lg={{ span: 16 }}
                            md={{ span: 16 }}
                            sm={{ span: 24 }}
                            xs={{ span: 24 }}
                          >
                            <Form.Item
                              name="email"
                              label="E-mail"
                              rules={[
                                {
                                  type: "email",
                                  message: "Formato do e-mail inválido",
                                },
                                {
                                  required: true,
                                  message: "Campo obrigatório.",
                                },
                              ]}
                            >
                              <Input
                                size="large"
                                maxLength={50}
                                placeholder="E-mail"
                                disabled
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={20} style={{ marginTop: 15 }}>
                          <Col
                            lg={{ span: 8 }}
                            md={{ span: 8 }}
                            sm={{ span: 24 }}
                            xs={{ span: 24 }}
                          >
                            <Form.Item
                              label="Documento"
                              name="settings_document_type"
                              rules={[
                                {
                                  required: true,
                                  message: "Campo obrigatório.",
                                },
                              ]}
                            >
                              <Select
                                size="large"
                                options={[
                                  { value: "CPF", label: "CPF" },
                                  { value: "CNPJ", label: "CNPJ" },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                          <Col
                            lg={{ span: 8 }}
                            md={{ span: 8 }}
                            sm={{ span: 24 }}
                            xs={{ span: 24 }}
                          >
                            <Form.Item
                              label={documentType}
                              name="settings_document"
                              rules={[
                                {
                                  required: true,
                                  message: "Campo obrigatório.",
                                },
                              ]}
                            >
                              <InputMask
                                mask={
                                  documentType === "CPF"
                                    ? "999.999.999-99"
                                    : "99.999.999/9999-99"
                                }
                                autoComplete="off"
                              >
                                <Input size="large" style={{ width: "100%" }} />
                              </InputMask>
                            </Form.Item>
                          </Col>
                        </Row>

                        <Title level={5}>Configurações</Title>

                        <Row gutter={20} style={{ marginTop: 15 }}>
                          <Col
                            lg={{ span: 12 }}
                            md={{ span: 12 }}
                            sm={{ span: 24 }}
                            xs={{ span: 24 }}
                          >
                            <Form.Item
                              label="Métodos de Pagamento"
                              name="settings_payment_methods"
                              rules={[
                                {
                                  required: true,
                                  message: "Campo obrigatório.",
                                },
                              ]}
                            >
                              <Select
                                mode="multiple"
                                size="large"
                                placeholder="Selecione os meios de pagamento"
                                options={paymentMethods}
                              />
                            </Form.Item>
                          </Col>
                          <Col
                            lg={{ span: 12 }}
                            md={{ span: 12 }}
                            sm={{ span: 12 }}
                            xs={{ span: 12 }}
                          >
                            <Form.Item
                              label="Lembrete de pagamento (dias)"
                              name="settings_email_frequency_days"
                              rules={[
                                {
                                  required: true,
                                  message: "Campo obrigatório.",
                                },
                              ]}
                            >
                              <Select
                                mode="multiple"
                                size="large"
                                placeholder="Selecione frequência de lembrete de pagamento"
                                options={[
                                  { value: 10, label: "10 dias antes" },
                                  { value: 5, label: "5 dias antes" },
                                  { value: 2, label: "2 dias antes" },
                                  { value: 0, label: "Dia do vencimento" },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <Row gutter={20} style={{ marginTop: 15 }}>
                          <Col
                            lg={{ span: 12 }}
                            md={{ span: 12 }}
                            sm={{ span: 24 }}
                            xs={{ span: 24 }}
                          >
                            <Space>
                              <Button
                                size="large"
                                onClick={() => setOpenAddressModal(true)}
                              >
                                Configurar endereço
                              </Button>
                              <Text type="secondary">
                                {addressData?.address
                                  ? "Endereço configurado"
                                  : "Sem endereço"}
                              </Text>
                            </Space>
                          </Col>
                          <Col
                            lg={{ span: 12 }}
                            md={{ span: 12 }}
                            sm={{ span: 24 }}
                            xs={{ span: 24 }}
                          >
                            <Space>
                              <Button
                                size="large"
                                onClick={() => setOpenBankModal(true)}
                              >
                                Configurar dados bancários
                              </Button>
                              <Text type="secondary">
                                {bankData?.account
                                  ? "Dados bancários configurados"
                                  : "Sem dados bancários"}
                              </Text>
                            </Space>
                          </Col>
                        </Row>

                        <Title level={5}>Sua Página</Title>

                        {accountUuid ? (
                          <Row>
                            <Col>
                              <a href={`/plans/${accountUuid}`} target="_blank">
                                Visitar página
                              </a>
                            </Col>
                          </Row>
                        ) : (
                          ""
                        )}
                      </Col>
                    </Row>
                  </Form>

                  <Row
                    gutter={20}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Col>
                      <Space>
                        {unblockForm && (
                          <Button
                            size="large"
                            loading={
                              createAccountLoading || updateAccountLoading
                            }
                            onClick={() => {
                              if (valuesChanged) {
                                setLeaveThePage(true);
                              } else {
                                setUnblockForm(false);
                              }
                            }}
                          >
                            Cancelar
                          </Button>
                        )}
                        <Button
                          type="primary"
                          size="large"
                          loading={createAccountLoading || updateAccountLoading}
                          onClick={
                            unblockForm
                              ? () => onSubmit()
                              : () => setUnblockForm(true)
                          }
                        >
                          {unblockForm ? "Gravar" : "Editar"}
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}

          <ConfirmModal
            open={leaveThePage}
            title="Tem certeza de que deseja cancelar as alterações?"
            content="Toda a informação não gravada será perdida."
            okText="Sair"
            cancelText="Voltar"
            onOk={() => {
              handleCancelEdit();
            }}
            onCancel={() => setLeaveThePage(false)}
          ></ConfirmModal>

          <Modal
            open={openAddressModal}
            title="Endereço"
            okText="Salvar"
            cancelText="Cancelar"
            onOk={() => {
              addressForm
                .validateFields()
                .then((values) => {
                  setAddressData(values);
                  setOpenAddressModal(false);
                  setValuesChanged(true);
                })
                .catch(() => null);
            }}
            onCancel={() => setOpenAddressModal(false)}
          >
            <Form form={addressForm} layout="vertical">
              <Form.Item
                label="CEP"
                name="postal_code"
                rules={[
                  { required: true, message: "Campo obrigatório." },
                  {
                    pattern: /^([\d]{5})-*([\d]{3})/,
                    message: "Formato do CEP inválido",
                  },
                ]}
              >
                <InputMask
                  mask={"99999-999"}
                  autoComplete="off"
                  onBlur={(event) => onChangeCep(event.target.value)}
                >
                  <Input size="large" style={{ width: "100%" }} />
                </InputMask>
              </Form.Item>
              <Form.Item
                label="Endereço"
                name="address"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                label="Número"
                name="number"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                label="Bairro"
                name="neighborhood"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                label="Estado"
                name="state"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                label="Cidade"
                name="city"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            open={openBankModal}
            title="Dados Bancários"
            okText="Salvar"
            cancelText="Cancelar"
            onOk={() => {
              bankForm
                .validateFields()
                .then((values) => {
                  setBankData(values);
                  setOpenBankModal(false);
                  setValuesChanged(true);
                })
                .catch(() => null);
            }}
            onCancel={() => setOpenBankModal(false)}
          >
            <Form form={bankForm} layout="vertical">
              <Form.Item
                label="Banco (código)"
                name="code"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" maxLength={4} />
              </Form.Item>
              <Form.Item
                label="Agência"
                name="agency"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" maxLength={5} />
              </Form.Item>
              <Form.Item
                label="Conta"
                name="account"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" maxLength={15} />
              </Form.Item>
              <Form.Item
                label="Tipo"
                name="type"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Select
                  size="large"
                  options={[
                    { value: "corrente", label: "Corrente" },
                    { value: "poupanca", label: "Poupança" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Titular"
                name="holder_name"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                label="Documento do Titular"
                name="holder_document"
                rules={[{ required: true, message: "Campo obrigatório." }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item label="Chave PIX" name="pix_key">
                <Input size="large" />
              </Form.Item>
            </Form>
          </Modal>
        </Card>
      </div>
    </>
  );
}
